import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Edit2, Trash2, Search, X, Loader2,
  RefreshCw, Image, Bold, Italic, Strikethrough,
  Type, Hash, Phone, Link2, ChevronDown, ChevronUp,
  Eye, Copy, Send, Sparkles, LayoutTemplate, Clock,
  MessageSquare, Check, AlertCircle, Paperclip, Smile,
  Video, FileText, ArrowRight, Play, Star, Zap, Users,
  BarChart2, CheckCheck, Package, ShoppingCart, Tag,
  Globe, Megaphone, Bot, Workflow,
} from 'lucide-react';
import { Input }    from '@/components/ui/input';
import { Label }    from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch }   from '@/components/ui/switch';
import { Badge }    from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api       from '@/lib/api';
import { toast } from 'sonner';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const TEMPLATE_CATEGORIES = [
  { value: 'marketing',     label: 'Marketing',        emoji: '📣', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'utility',       label: 'Utility',          emoji: '⚙️',  color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'authentication',label: 'Authentication',   emoji: '🔐', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'ecommerce',     label: 'E-commerce',       emoji: '🛒', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'support',       label: 'Customer Support', emoji: '🎧', color: 'bg-pink-100 text-pink-700 border-pink-200' },
  { value: 'reminder',      label: 'Reminder',         emoji: '⏰', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
];

const HEADER_TYPES = [
  { value: 'none',     label: 'No Header',    icon: X },
  { value: 'text',     label: 'Text',         icon: Type },
  { value: 'image',    label: 'Image',        icon: Image },
  { value: 'video',    label: 'Video',        icon: Video },
  { value: 'document', label: 'Document',     icon: FileText },
];

const BUTTON_TYPES = [
  { value: 'none',          label: 'No Buttons' },
  { value: 'cta',           label: 'Call to Action' },
  { value: 'quick_reply',   label: 'Quick Replies' },
];

const LANGUAGES = [
  { value: 'en',    label: 'English' },
  { value: 'hi',    label: 'Hindi' },
  { value: 'en_IN', label: 'English (India)' },
  { value: 'mr',    label: 'Marathi' },
  { value: 'ta',    label: 'Tamil' },
  { value: 'te',    label: 'Telugu' },
];

const STATUS_CONFIG = {
  approved:  { label: 'Approved',  color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  pending:   { label: 'Pending',   color: 'bg-yellow-50 text-yellow-700 border-yellow-200',   dot: 'bg-yellow-500' },
  rejected:  { label: 'Rejected',  color: 'bg-red-50 text-red-700 border-red-200',            dot: 'bg-red-500' },
  draft:     { label: 'Draft',     color: 'bg-gray-50 text-gray-600 border-gray-200',         dot: 'bg-gray-400' },
};

const STARTER_TEMPLATES = [
  {
    id: 's1',
    name: 'Flash Sale Offer',
    category: 'marketing',
    headerType: 'image',
    headerText: '',
    body: `🔥 *Flash Sale Alert!* 🔥\n\nHey {{1}},\n\nDon't miss our biggest sale of the year!\n\n👉 *{{2}}* – only for *{{3}}* more hours!\n\n✅ Up to *70% OFF*\n✅ Free delivery above ₹499\n✅ Easy returns\n\n_Hurry! Limited stock available._`,
    footer: 'Reply STOP to unsubscribe',
    buttonType: 'cta',
    buttons: [{ type: 'url', text: 'Grab Them Now', value: 'https://yourstore.com/sale' }],
    language: 'en',
  },
  {
    id: 's2',
    name: 'Order Confirmed',
    category: 'ecommerce',
    headerType: 'text',
    headerText: '✅ Order Confirmed!',
    body: `Hi *{{1}}*,\n\nYour order has been placed successfully! 🎉\n\n📦 *Order ID:* {{2}}\n🛍️ *Items:* {{3}}\n💰 *Total:* ₹{{4}}\n\n🚚 *Estimated Delivery:* {{5}}\n\nWe'll notify you when your order is shipped.\n\n_Thank you for shopping with us!_`,
    footer: 'Need help? Reply to this message',
    buttonType: 'cta',
    buttons: [{ type: 'url', text: 'Track Order', value: 'https://yourstore.com/track' }],
    language: 'en',
  },
  {
    id: 's3',
    name: 'Appointment Reminder',
    category: 'reminder',
    headerType: 'text',
    headerText: '📅 Appointment Reminder',
    body: `Hello *{{1}}*,\n\nThis is a reminder for your upcoming appointment:\n\n🏥 *{{2}}*\n📅 *Date:* {{3}}\n🕐 *Time:* {{4}}\n📍 *Location:* {{5}}\n\nPlease arrive 10 minutes early.\n\n_To reschedule, reply with "RESCHEDULE"_`,
    footer: 'Powered by TrueChattr',
    buttonType: 'quick_reply',
    buttons: [
      { type: 'quick_reply', text: 'Confirm ✅', value: 'CONFIRM' },
      { type: 'quick_reply', text: 'Reschedule 🔄', value: 'RESCHEDULE' },
      { type: 'quick_reply', text: 'Cancel ❌', value: 'CANCEL' },
    ],
    language: 'en',
  },
  {
    id: 's4',
    name: 'OTP Verification',
    category: 'authentication',
    headerType: 'none',
    headerText: '',
    body: `*{{1}}* is your verification code for *{{2}}*.\n\n⏱️ Valid for *10 minutes* only.\n\n🔒 For your security, never share this OTP with anyone.\n\n_If you didn't request this, please ignore._`,
    footer: 'This is an automated message',
    buttonType: 'none',
    buttons: [],
    language: 'en',
  },
  {
    id: 's5',
    name: 'Support Ticket Update',
    category: 'support',
    headerType: 'text',
    headerText: '🎧 Support Update',
    body: `Hi *{{1}}*,\n\nYour support ticket has been updated!\n\n🎫 *Ticket ID:* #{{2}}\n📋 *Status:* {{3}}\n💬 *Message:*\n_{{4}}_\n\nOur team is working on it and will get back to you within *{{5}} hours*.`,
    footer: 'Reply to this message to respond',
    buttonType: 'quick_reply',
    buttons: [
      { type: 'quick_reply', text: 'View Ticket', value: 'VIEW_TICKET' },
      { type: 'quick_reply', text: 'Talk to Agent', value: 'AGENT' },
    ],
    language: 'en',
  },
];

// ✅ CHANGE 1: keywordId add kiya EMPTY_FORM mein
const EMPTY_FORM = {
  name: '',
  category: 'marketing',
  language: 'en',
  headerType: 'none',
  headerText: '',
  headerMediaUrl: '',
  body: '',
  footer: '',
  buttonType: 'none',
  buttons: [],
  isActive: true,
  keywordId: '',  // ✅ ADDED
};

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1], delay },
});

// ─────────────────────────────────────────────────────────────────────────────
// WHATSAPP PHONE PREVIEW
// ─────────────────────────────────────────────────────────────────────────────

function WhatsAppPreview({ template, previewVars = {} }) {
  const renderBody = (text) => {
    if (!text) return '';
    let rendered = text;
    rendered = rendered.replace(/\{\{(\d+)\}\}/g, (_, n) => {
      return previewVars[n] || `[Variable ${n}]`;
    });
    rendered = rendered.replace(/\*(.*?)\*/g, '<strong>$1</strong>');
    rendered = rendered.replace(/_(.*?)_/g, '<em>$1</em>');
    rendered = rendered.replace(/~(.*?)~/g, '<del>$1</del>');
    rendered = rendered.replace(/\n/g, '<br/>');
    return rendered;
  };

  const catConfig = TEMPLATE_CATEGORIES.find(c => c.value === template.category);
  const now = new Date();
  const timeStr = `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-[320px] bg-gray-900 rounded-[40px] p-3 shadow-2xl border-4 border-gray-800">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-gray-900 rounded-b-2xl z-10" />
        <div className="bg-[#ECE5DD] rounded-[30px] overflow-hidden">
          <div className="bg-[#075E54] px-4 py-3 flex items-center gap-3 pt-8">
            <div className="w-9 h-9 rounded-full bg-emerald-400 flex items-center justify-center text-white font-bold text-sm">TC</div>
            <div>
              <p className="text-white text-sm font-semibold">TrueChattr</p>
              <p className="text-emerald-200 text-xs">Business Account</p>
            </div>
          </div>
          <div
            className="min-h-[420px] p-3 space-y-2 relative"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23b2dfdb' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          >
            <div className="flex justify-start">
              <div className="max-w-[85%] bg-white rounded-2xl rounded-tl-sm shadow-sm overflow-hidden">
                {template.headerType === 'image' && (
                  <div className="relative">
                    {template.headerMediaUrl ? (
                      <img src={template.headerMediaUrl} alt="header" className="w-full h-40 object-cover" />
                    ) : (
                      <div className="w-full h-40 bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                        <Image className="w-10 h-10 text-white/60" />
                        <span className="ml-2 text-white/80 text-sm">Image Header</span>
                      </div>
                    )}
                  </div>
                )}
                {template.headerType === 'video' && (
                  <div className="w-full h-40 bg-gray-800 flex items-center justify-center relative">
                    <Video className="w-8 h-8 text-white/60" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                        <Play className="w-6 h-6 text-white ml-1" />
                      </div>
                    </div>
                  </div>
                )}
                {template.headerType === 'document' && (
                  <div className="bg-gray-100 px-4 py-3 flex items-center gap-3 border-b">
                    <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-700">Document.pdf</p>
                      <p className="text-xs text-gray-400">PDF • Tap to open</p>
                    </div>
                  </div>
                )}
                {template.headerType === 'text' && template.headerText && (
                  <div className="px-3 pt-3 pb-1">
                    <p className="font-bold text-gray-900 text-sm" dangerouslySetInnerHTML={{ __html: renderBody(template.headerText) }} />
                  </div>
                )}
                <div className="px-3 py-2">
                  <p
                    className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{ __html: renderBody(template.body) || '<span class="text-gray-400 italic">Your message will appear here...</span>' }}
                  />
                </div>
                {template.footer && (
                  <div className="px-3 pb-2">
                    <p className="text-xs text-gray-400">{template.footer}</p>
                  </div>
                )}
                <div className="px-3 pb-2 flex justify-end">
                  <span className="text-[10px] text-gray-400 flex items-center gap-1">
                    {timeStr} <CheckCheck className="w-3 h-3 text-blue-500" />
                  </span>
                </div>
                {template.buttonType === 'cta' && template.buttons?.length > 0 && (
                  <div className="border-t border-gray-100">
                    {template.buttons.map((btn, i) => (
                      <button key={i} className="w-full py-2.5 text-center text-[#128C7E] text-sm font-semibold hover:bg-gray-50 flex items-center justify-center gap-2 border-b border-gray-100 last:border-b-0">
                        {btn.type === 'url' && <Link2 className="w-3.5 h-3.5" />}
                        {btn.type === 'phone' && <Phone className="w-3.5 h-3.5" />}
                        {btn.text || 'Button'}
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {template.buttonType === 'quick_reply' && template.buttons?.length > 0 && (
              <div className="flex flex-wrap gap-2 pl-1">
                {template.buttons.map((btn, i) => (
                  <div key={i} className="bg-white rounded-full px-3 py-1.5 text-[#128C7E] text-xs font-semibold shadow-sm border border-gray-100">
                    {btn.text || `Reply ${i + 1}`}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-[#F0F0F0] px-3 py-2 flex items-center gap-2">
            <div className="flex-1 bg-white rounded-full px-4 py-2 text-xs text-gray-400 flex items-center">
              <Smile className="w-4 h-4 text-gray-400 mr-2" />
              Type a message
            </div>
            <div className="w-8 h-8 rounded-full bg-[#128C7E] flex items-center justify-center">
              <Send className="w-4 h-4 text-white" />
            </div>
          </div>
        </div>
      </div>
      {catConfig && (
        <div className={`mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium ${catConfig.color}`}>
          <span>{catConfig.emoji}</span> {catConfig.label}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// VARIABLE TOOLBAR
// ─────────────────────────────────────────────────────────────────────────────

function FormatToolbar({ onInsert }) {
  const tools = [
    { label: 'Bold',          icon: Bold,          wrap: ['*', '*'],   example: '*text*' },
    { label: 'Italic',        icon: Italic,        wrap: ['_', '_'],   example: '_text_' },
    { label: 'Strikethrough', icon: Strikethrough, wrap: ['~', '~'],   example: '~text~' },
    { label: 'Variable',      icon: Hash,          wrap: ['{{', '}}'], example: '{{1}}' },
  ];
  return (
    <div className="flex items-center gap-1 p-2 bg-gray-50 border border-gray-200 rounded-xl mb-2">
      <span className="text-xs text-gray-400 mr-1 font-medium">Format:</span>
      {tools.map((t) => (
        <button
          key={t.label}
          type="button"
          title={`${t.label} — ${t.example}`}
          onClick={() => onInsert(t.wrap)}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-white hover:border hover:border-gray-200 hover:shadow-sm transition-all"
        >
          <t.icon className="w-3.5 h-3.5" />
        </button>
      ))}
      <div className="ml-auto flex items-center gap-1 text-xs text-gray-400">
        <span className="px-1.5 py-0.5 bg-gray-200 rounded text-[10px]">{'{{1}}'}</span>
        <span>= dynamic variable</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BUTTON EDITOR
// ─────────────────────────────────────────────────────────────────────────────

function ButtonEditor({ buttonType, buttons, onChange }) {
  const addButton = () => {
    if (buttonType === 'cta') {
      if (buttons.length >= 2) { toast.error('Max 2 CTA buttons'); return; }
      onChange([...buttons, { type: 'url', text: '', value: '' }]);
    } else {
      if (buttons.length >= 3) { toast.error('Max 3 quick reply buttons'); return; }
      onChange([...buttons, { type: 'quick_reply', text: '', value: '' }]);
    }
  };
  const updateButton = (i, field, value) => {
    const updated = [...buttons];
    updated[i] = { ...updated[i], [field]: value };
    onChange(updated);
  };
  const removeButton = (i) => onChange(buttons.filter((_, idx) => idx !== i));
  if (buttonType === 'none') return null;
  return (
    <div className="space-y-3">
      <AnimatePresence>
        {buttons.map((btn, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {buttonType === 'cta' ? `Button ${i + 1}` : `Quick Reply ${i + 1}`}
              </span>
              <button onClick={() => removeButton(i)} className="p-1 rounded-lg hover:bg-red-50 hover:text-red-500 text-gray-400 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Button Text</Label>
                <Input
                  value={btn.text}
                  onChange={e => updateButton(i, 'text', e.target.value)}
                  placeholder={buttonType === 'cta' ? 'Shop Now' : 'Yes, confirm'}
                  maxLength={25}
                  className="h-9 text-sm rounded-lg"
                />
              </div>
              {buttonType === 'cta' && (
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">Type</Label>
                  <Select value={btn.type} onValueChange={v => updateButton(i, 'type', v)}>
                    <SelectTrigger className="h-9 text-sm rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="url"><Link2 className="w-3.5 h-3.5 inline mr-1" />URL</SelectItem>
                      <SelectItem value="phone"><Phone className="w-3.5 h-3.5 inline mr-1" />Phone</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {buttonType === 'cta' && (
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">{btn.type === 'url' ? 'URL' : 'Phone Number'}</Label>
                <Input
                  value={btn.value}
                  onChange={e => updateButton(i, 'value', e.target.value)}
                  placeholder={btn.type === 'url' ? 'https://yoursite.com' : '+91 XXXXX XXXXX'}
                  className="h-9 text-sm rounded-lg"
                />
              </div>
            )}
            {buttonType === 'quick_reply' && (
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Reply Payload (optional)</Label>
                <Input
                  value={btn.value}
                  onChange={e => updateButton(i, 'value', e.target.value)}
                  placeholder="CONFIRM or YES"
                  className="h-9 text-sm rounded-lg"
                />
              </div>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
      <button
        onClick={addButton}
        type="button"
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-gray-300 text-sm font-medium text-gray-500 hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50/50 transition-all"
      >
        <Plus className="w-4 h-4" />
        Add {buttonType === 'cta' ? 'CTA Button' : 'Quick Reply'}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE CARD
// ─────────────────────────────────────────────────────────────────────────────

function TemplateCard({ template, keywords, onEdit, onDelete, onDuplicate, isDeleting }) {
  const [expanded, setExpanded] = useState(false);
  const catConfig = TEMPLATE_CATEGORIES.find(c => c.value === template.category) || TEMPLATE_CATEGORIES[0];
  const statusCfg = STATUS_CONFIG[template.status || 'draft'];
   // ✅ FIX: template_text ya body dono check karo
  const bodyText = template.template_text || template.body || ''
  const varCount = (bodyText.match(/\{\{\d+\}\}/g) || []).length;
  const btnCount  = template.buttons?.length || 0;

  // ✅ CHANGE 2: keyword naam dikhane ke liye
  const linkedKeyword = keywords?.find(k => k.id === template.keyword_id);

  const previewText = bodyText.replace(/\*(.*?)\*/g, '$1').replace(/_(.*?)_/g, '$1').replace(/\n/g, ' ').slice(0, 100);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:border-emerald-200 transition-all duration-300 overflow-hidden"
    >
      <div className={`h-1 w-full ${template.category === 'marketing' ? 'bg-gradient-to-r from-purple-400 to-pink-400' : template.category === 'ecommerce' ? 'bg-gradient-to-r from-emerald-400 to-teal-400' : template.category === 'authentication' ? 'bg-gradient-to-r from-orange-400 to-red-400' : template.category === 'support' ? 'bg-gradient-to-r from-pink-400 to-rose-400' : template.category === 'reminder' ? 'bg-gradient-to-r from-yellow-400 to-amber-400' : 'bg-gradient-to-r from-blue-400 to-indigo-400'}`} />
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-bold text-gray-900 text-sm truncate">{template.name}</h3>
              {template.isActive === false && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">Inactive</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${catConfig.color}`}>
                {catConfig.emoji} {catConfig.label}
              </span>
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-medium ${statusCfg.color}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
                {statusCfg.label}
              </span>
              {/* ✅ CHANGE 2: Linked keyword chip dikhao card pe */}
              {linkedKeyword && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium bg-violet-50 text-violet-700 border-violet-200">
                  🔑 {linkedKeyword.keyword}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={() => onDuplicate(template)} className="p-1.5 rounded-lg hover:bg-blue-50 hover:text-blue-600 text-gray-400 transition-colors" title="Duplicate">
              <Copy className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => onEdit(template)} className="p-1.5 rounded-lg hover:bg-emerald-50 hover:text-emerald-600 text-gray-400 transition-colors" title="Edit">
              <Edit2 className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => onDelete(template.id)} disabled={isDeleting} className="p-1.5 rounded-lg hover:bg-red-50 hover:text-red-500 text-gray-400 transition-colors disabled:opacity-50" title="Delete">
              {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {template.headerType && template.headerType !== 'none' && (
            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
              {template.headerType === 'image' && <Image className="w-3 h-3" />}
              {template.headerType === 'video' && <Video className="w-3 h-3" />}
              {template.headerType === 'document' && <FileText className="w-3 h-3" />}
              {template.headerType === 'text' && <Type className="w-3 h-3" />}
              {template.headerType}
            </span>
          )}
          {varCount > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
              <Hash className="w-3 h-3" /> {varCount} var{varCount > 1 ? 's' : ''}
            </span>
          )}
          {btnCount > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
              <MessageSquare className="w-3 h-3" /> {btnCount} btn{btnCount > 1 ? 's' : ''}
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
            <Globe className="w-3 h-3" />
            {LANGUAGES.find(l => l.value === template.language)?.label || 'English'}
          </span>
        </div>
        <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mb-3">
          {previewText || 'No body text yet.'}
          {bodyText.length > 100 ? '...' : ''}
        </p>
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-center gap-1 py-1.5 text-xs text-gray-400 hover:text-emerald-600 transition-colors"
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {expanded ? 'Show less' : 'Preview'}
        </button>
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >

            <div className="pt-3 border-t border-gray-100">
              <WhatsAppPreview template={{ ...template, body: bodyText }} />
            </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function WhatsAppTemplates() {
  const [templates,    setTemplates]    = useState([]);
  // ✅ CHANGE 3: keywords state add kiya
  const [keywords,     setKeywords]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [dialogOpen,   setDialogOpen]   = useState(false);
  const [editing,      setEditing]      = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [deletingId,   setDeletingId]   = useState(null);
  const [search,       setSearch]       = useState('');
  const [catFilter,    setCatFilter]    = useState('all');
  const [form,         setForm]         = useState(EMPTY_FORM);
  const [activeTab,    setActiveTab]    = useState('editor');
  const [previewVars,  setPreviewVars]  = useState({});
  const [imgPreview,   setImgPreview]   = useState(false);
  const [starterOpen,  setStarterOpen]  = useState(false);
  const bodyRef = useRef(null);

  // ✅ CHANGE 3: fetchKeywords bhi call karo useEffect mein
  useEffect(() => {
    fetchTemplates();
    fetchKeywords();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/business/templates');
      setTemplates(data.templates || []);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ CHANGE 3: keywords fetch function
  const fetchKeywords = async () => {
    try {
      const { data } = await api.get('/business/keywords');
      setKeywords(data.keywords || []);
    } catch {
      setKeywords([]);
    }
  };

  const approvedCount = templates.filter(t => t.status === 'approved').length;
  const totalSent     = templates.reduce((s, t) => s + (t.sentCount || 0), 0);

  const filtered = templates.filter(t => {
    const matchSearch = t.name?.toLowerCase().includes(search.toLowerCase()) ||
                    (t.template_text || t.body || '').toLowerCase().includes(search.toLowerCase());
    const matchCat = catFilter === 'all' || t.category === catFilter;
    return matchSearch && matchCat;
  });


  const detectedVars = useMemo(() => {
  return [...new Set(
    [...(form.body?.matchAll(/\{\{(\d+)\}\}/g) || [])]
      .map(m => m[1])
      .sort((a, b) => Number(a) - Number(b))
  )];
}, [form.body]);

const handleInsert = useCallback((wrap) => {
  const el = bodyRef.current;
  if (!el) return;
  const start = el.selectionStart;
  const end   = el.selectionEnd;
  const selected = form.body.substring(start, end);
  let insertion;
  if (wrap[0] === '{{') {
    const nextVar = (detectedVars.length > 0 ? Math.max(...detectedVars.map(Number)) + 1 : 1).toString();
    insertion = `{{${nextVar}}}`;
  } else {
    insertion = `${wrap[0]}${selected || 'text'}${wrap[1]}`;
  }
  const newBody = form.body.substring(0, start) + insertion + form.body.substring(end);
  setForm(f => ({ ...f, body: newBody }));
  setTimeout(() => {
    el.focus();
    el.setSelectionRange(start + insertion.length, start + insertion.length);
  }, 0);
}, [form.body, detectedVars]);

const handleSave = async () => {
  if (!form.name?.trim() || form.name.trim().length < 2) {
    toast.error('Template name must be at least 2 characters'); return;
  }
  if (!form.body?.trim() || form.body.trim().length < 10) {
    toast.error('Message body must be at least 10 characters'); return;
  }
  if (!form.keywordId) {
    toast.error('Please select a trigger keyword for this template'); return;
  }

  setSaving(true);
  try {
    const payload = {
      name: form.name.trim(),
      category: form.category,
      language: form.language,
      headerType: form.headerType,
      headerText: form.headerType === 'text' ? form.headerText : '',
      headerMediaUrl: ['image','video','document'].includes(form.headerType) ? form.headerMediaUrl : '',
      body: form.body.trim(),
      footer: form.footer?.trim() || '',
      buttonType: form.buttonType,
      buttons: form.buttonType !== 'none' ? form.buttons : [],
      isActive: form.isActive,
      status: 'pending',
      keywordId: form.keywordId,
      variables: previewVars,
    };

    if (editing) {
      await api.put(`/business/templates/${editing.id}`, payload);
      toast.success('Template updated!');
    } else {
      await api.post('/business/templates', payload);
      toast.success('Template submitted for approval!');
    }
    closeDialog();
    fetchTemplates();
  } catch (err) {
    toast.error(err.response?.data?.error || 'Failed to save template');
  } finally {
    setSaving(false);
  }
};

  

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    setDeletingId(id);
    try {
      await api.delete(`/business/templates/${id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  // ✅ CHANGE 5: handleDuplicate mein keywordId add kiya
  const handleDuplicate = (template) => {
    setEditing(null);
    setForm({
      name: `${template.name} (Copy)`,
      category: template.category,
      language: template.language || 'en',
      headerType: template.headerType || 'none',
      headerText: template.headerText || '',
      headerMediaUrl: template.headerMediaUrl || '',
      body: template.template_text || template.body || '',  // ← FIX
      footer: template.footer || '',
      buttonType: template.buttonType || 'none',
      buttons: template.buttons ? [...template.buttons] : [],
      isActive: true,
      keywordId: template.keyword_id || '',  // ✅ ADDED
    });
    setActiveTab('editor');
    setPreviewVars({});
    setDialogOpen(true);
  };

  const loadStarter = (starter) => {
    setEditing(null);
    setForm({
      name: starter.name,
      category: starter.category,
      language: starter.language || 'en',
      headerType: starter.headerType || 'none',
      headerText: starter.headerText || '',
      headerMediaUrl: '',
      body: starter.body,
      footer: starter.footer || '',
      buttonType: starter.buttonType || 'none',
      buttons: starter.buttons ? [...starter.buttons] : [],
      isActive: true,
      keywordId: '',  // ✅ ADDED: starter mein blank rakho, user select kare
    });
    setStarterOpen(false);
    setActiveTab('editor');
    setPreviewVars({});
    setDialogOpen(true);
  };

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setActiveTab('editor');
    setPreviewVars({});
    setDialogOpen(true);
  };

  // ✅ CHANGE 6: openEdit mein keywordId set kiya
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || '',
      category: t.category || 'marketing',
      language: t.language || 'en',
      headerType: t.header_type || t.headerType || 'none',  // ← snake_case bhi check karo
      headerText: t.headerText || '',
      headerMediaUrl: t.headerMediaUrl || '',
      body: t.template_text || t.body || '',  // ← YAHI FIX HAI
      footer: t.footer || '',
      buttonType: t.buttonType || 'none',
      buttons: t.buttons ? [...t.buttons] : [],
      isActive: t.isActive !== undefined ? t.isActive : true,
      keywordId: t.keyword_id || '',  // ✅ ADDED
    });
    setActiveTab('editor');
    setPreviewVars({});
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    setPreviewVars({});
  };

  if (loading && !templates.length) {
    return (
      <div className="h-[calc(100vh-140px)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative w-14 h-14">
            <div className="absolute inset-0 rounded-full border-4 border-emerald-100" />
            <div className="absolute inset-0 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" />
          </div>
          <p className="text-sm text-gray-400 font-medium">Loading templates…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">

      {/* ── Header ── */}
      <motion.div {...fadeUp(0)} className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight flex items-center gap-2">
            <LayoutTemplate className="w-7 h-7 text-emerald-600" />
            Message Templates
          </h1>
          <p className="mt-1 text-sm text-gray-400">
            Create WhatsApp-approved message templates for broadcasts & automation
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={fetchTemplates}
            disabled={loading}
            className="p-2 rounded-xl border border-gray-200 bg-white text-gray-500 hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50 transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setStarterOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100 transition-all"
          >
            <Sparkles className="w-4 h-4" />
            Use Starter
          </button>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-lg shadow-emerald-200/60 transition-all"
          >
            <Plus className="w-4 h-4" />
            New Template
          </button>
        </div>
      </motion.div>

      {/* ── Stats ── */}
      <motion.div {...fadeUp(0.06)} className="flex flex-wrap gap-3">
        {[
          { icon: LayoutTemplate, label: 'Total Templates', value: templates.length },
          { icon: Check,          label: 'Approved',         value: approvedCount },
          { icon: Send,           label: 'Total Sent',       value: totalSent.toLocaleString() },
          { icon: Clock,          label: 'Pending Review',   value: templates.filter(t => t.status === 'pending').length },
        ].map(({ icon: Icon, label, value }) => (
          <div key={label} className="flex items-center gap-2.5 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm">
            <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
              <Icon className="w-3.5 h-3.5 text-emerald-600" />
            </div>
            <div>
              <span className="text-xs text-gray-400 block leading-none mb-0.5">{label}</span>
              <span className="text-sm font-bold text-gray-900 dark:text-white">{value}</span>
            </div>
          </div>
        ))}
      </motion.div>

      {/* ── Search + Filter ── */}
      <motion.div {...fadeUp(0.1)} className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <Input
            placeholder="Search templates…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 h-10 rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 text-sm"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-48 h-10 rounded-xl border-gray-200 text-sm">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {TEMPLATE_CATEGORIES.map(c => (
              <SelectItem key={c.value} value={c.value}>{c.emoji} {c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(search || catFilter !== 'all') && (
          <span className="self-center text-xs text-gray-400">
            {filtered.length} result{filtered.length !== 1 ? 's' : ''}
          </span>
        )}
      </motion.div>

      {/* ── Info Banner ── */}
      <motion.div {...fadeUp(0.12)} className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
        <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">WhatsApp Template Guidelines</p>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
            Templates must be approved by WhatsApp before use. Use <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{'{{1}}'}</code>, <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{'{{2}}'}</code> for dynamic variables. Avoid promotional language in Utility/Auth templates.
          </p>
        </div>
      </motion.div>

      {/* ── Grid ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <AnimatePresence>
          {filtered.length > 0 ? (
            filtered.map((t) => (
              <TemplateCard
                key={t.id}
                template={t}
                keywords={keywords}  // ✅ ADDED: keywords pass karo card ko
                onEdit={openEdit}
                onDelete={handleDelete}
                onDuplicate={handleDuplicate}
                isDeleting={deletingId === t.id}
              />
            ))
          ) : (
            <motion.div
              key="empty"
              {...fadeUp(0.1)}
              className="col-span-full text-center py-20 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm"
            >
              <div className="w-20 h-20 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center mb-5">
                <LayoutTemplate className="w-10 h-10 text-emerald-300" />
              </div>
              <p className="text-gray-900 dark:text-white font-bold text-lg mb-2">
                {search || catFilter !== 'all' ? 'No templates found' : 'No templates yet'}
              </p>
              <p className="text-sm text-gray-400 mb-6 max-w-sm mx-auto">
                {search
                  ? `No results for "${search}"`
                  : 'Create your first WhatsApp message template to start automating customer conversations.'}
              </p>
              {!search && catFilter === 'all' && (
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => setStarterOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-emerald-300 bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100 transition-colors"
                  >
                    <Sparkles className="w-4 h-4" /> Use Starter
                  </button>
                  <button
                    onClick={openAdd}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Create Template
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          STARTER TEMPLATES MODAL
      ═══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {starterOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setStarterOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden border border-gray-200 dark:border-gray-700"
            >
              <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-emerald-600" /> Starter Templates
                  </h2>
                  <p className="text-sm text-gray-400 mt-0.5">Choose a pre-built template to get started quickly</p>
                </div>
                <button onClick={() => setStarterOpen(false)} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-100px)] grid grid-cols-1 sm:grid-cols-2 gap-4">
                {STARTER_TEMPLATES.map((starter) => {
                  const cat = TEMPLATE_CATEGORIES.find(c => c.value === starter.category);
                  return (
                    <button
                      key={starter.id}
                      onClick={() => loadStarter(starter)}
                      className="text-left p-5 rounded-xl border-2 border-gray-100 hover:border-emerald-300 hover:shadow-md hover:bg-emerald-50/30 transition-all duration-200 group"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${cat?.color}`}>
                          {cat?.emoji} {cat?.label}
                        </span>
                        {starter.headerType !== 'none' && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                            {starter.headerType}
                          </span>
                        )}
                      </div>
                      <h3 className="font-bold text-gray-900 dark:text-white text-sm mb-1 group-hover:text-emerald-700">{starter.name}</h3>
                      <p className="text-xs text-gray-400 line-clamp-2 leading-relaxed">
                        {starter.body.replace(/\*(.*?)\*/g, '$1').replace(/\n/g, ' ').slice(0, 80)}...
                      </p>
                      <div className="mt-3 flex items-center gap-1.5">
                        {starter.buttonType !== 'none' && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded-full">
                            {starter.buttons.length} button{starter.buttons.length > 1 ? 's' : ''}
                          </span>
                        )}
                        <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-full">
                          {(starter.body.match(/\{\{\d+\}\}/g) || []).length} variables
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════════════════════════════════════════
          ADD / EDIT DIALOG
      ═══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={dialogOpen} onOpenChange={open => !open && closeDialog()}>
        <DialogContent className="sm:max-w-5xl max-h-[95vh] overflow-hidden p-0 rounded-2xl border-gray-100 shadow-2xl">
          <div className="flex flex-col h-full max-h-[95vh]">

            <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
              <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-emerald-600" />
                {editing ? 'Edit Template' : 'Create Template'}
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-1 overflow-hidden min-h-0">

              {/* ── Left: Editor ── */}
              <div className="flex-1 overflow-y-auto p-6 space-y-5 border-r border-gray-100">

                {/* Name + Category + Language */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-1 space-y-1.5">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Template Name <span className="text-rose-400">*</span>
                    </Label>
                    <Input
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="flash_sale_offer"
                      className="h-10 rounded-xl text-sm"
                    />
                    <p className="text-[11px] text-gray-400">Lowercase, underscores only</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Category</Label>
                    <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                      <SelectTrigger className="h-10 rounded-xl text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TEMPLATE_CATEGORIES.map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.emoji} {c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Language</Label>
                    <Select value={form.language} onValueChange={v => setForm(f => ({ ...f, language: v }))}>
                      <SelectTrigger className="h-10 rounded-xl text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LANGUAGES.map(l => (
                          <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* ✅ CHANGE 7: Keyword dropdown — Name/Category ke baad, Header ke pehle */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Trigger Keyword <span className="text-rose-400">*</span>
                  </Label>
                  <Select value={form.keywordId} onValueChange={v => setForm(f => ({ ...f, keywordId: v }))}>
                    <SelectTrigger className="h-10 rounded-xl text-sm">
                      <SelectValue placeholder="Select keyword that triggers this template..." />
                    </SelectTrigger>
                    <SelectContent>
                      {keywords.length === 0 ? (
                        <div className="px-3 py-4 text-center text-sm text-gray-400">
                          No keywords found. Please add keywords first.
                        </div>
                      ) : (
                        keywords.map(k => (
                          <SelectItem key={k.id} value={k.id}>
                            🔑 {k.keyword}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-gray-400">
                    This template will auto-send when customer types the selected keyword
                  </p>
                  {/* ✅ Keywords nahi hain toh warning dikhao */}
                  {keywords.length === 0 && (
                    <div className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                      <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <p className="text-xs text-amber-700">
                        No keywords added yet. Go to <strong>Keywords</strong> section first to add trigger words.
                      </p>
                    </div>
                  )}
                </div>

                {/* ── HEADER ── */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Header (Optional)</Label>
                  <div className="flex flex-wrap gap-2">
                    {HEADER_TYPES.map(ht => (
                      <button
                        key={ht.value}
                        type="button"
                        onClick={() => setForm(f => ({ ...f, headerType: ht.value }))}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-medium transition-all ${form.headerType === ht.value ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                      >
                        <ht.icon className="w-3.5 h-3.5" />
                        {ht.label}
                      </button>
                    ))}
                  </div>
                  {form.headerType === 'text' && (
                    <Input
                      value={form.headerText}
                      onChange={e => setForm(f => ({ ...f, headerText: e.target.value }))}
                      placeholder="✅ Order Confirmed!"
                      maxLength={60}
                      className="h-10 rounded-xl text-sm"
                    />
                  )}
                  {['image', 'video', 'document'].includes(form.headerType) && (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          value={form.headerMediaUrl}
                          onChange={e => {
                            setForm(f => ({ ...f, headerMediaUrl: e.target.value }));
                            setImgPreview(false);
                          }}
                          placeholder={form.headerType === 'image' ? 'https://cdn.yoursite.com/banner.jpg' : form.headerType === 'video' ? 'https://cdn.yoursite.com/promo.mp4' : 'https://cdn.yoursite.com/brochure.pdf'}
                          className="flex-1 h-10 rounded-xl text-sm"
                        />
                        {form.headerType === 'image' && form.headerMediaUrl && (
                          <button
                            type="button"
                            onClick={() => setImgPreview(v => !v)}
                            className="px-3 py-2 rounded-xl border border-gray-200 text-xs text-gray-600 hover:border-emerald-300 hover:text-emerald-600 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <AnimatePresence>
                        {imgPreview && form.headerMediaUrl && form.headerType === 'image' && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden rounded-xl border border-gray-200"
                          >
                            <img
                              src={form.headerMediaUrl}
                              alt="Header preview"
                              className="w-full h-36 object-cover"
                              onError={() => { setImgPreview(false); toast.error('Could not load image'); }}
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>
                      <p className="text-xs text-gray-400">
                        {form.headerType === 'image' ? '🖼 JPG/PNG, max 5MB.' : form.headerType === 'video' ? '🎥 MP4, max 16MB.' : '📄 PDF format recommended.'}
                      </p>
                    </div>
                  )}
                </div>

                {/* ── BODY ── */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Message Body <span className="text-rose-400">*</span>
                    <span className="ml-2 text-xs font-normal text-gray-400">{form.body?.length || 0}/1024</span>
                  </Label>
                  <FormatToolbar onInsert={handleInsert} />
                  <Textarea
                    ref={bodyRef}
                    value={form.body}
                    onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                    placeholder={`Hi *{{1}}*,\n\nYour order *{{2}}* has been confirmed! 🎉\n\n_Thank you for shopping with us._`}
                    rows={7}
                    maxLength={1024}
                    className="rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 text-sm resize-none font-mono leading-relaxed"
                  />
                  {detectedVars.length > 0 && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                      <p className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1">
                        <Hash className="w-3.5 h-3.5" /> Fill preview values for variables:
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {detectedVars.map(v => (
                          <div key={v} className="flex items-center gap-2">
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-mono">{`{{${v}}}`}</span>
                            <Input
                              value={previewVars[v] || ''}
                              onChange={e => setPreviewVars(p => ({ ...p, [v]: e.target.value }))}
                              placeholder={`Value ${v}`}
                              className="h-7 text-xs rounded-lg flex-1"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* ── FOOTER ── */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Footer <span className="text-xs font-normal text-gray-400">(optional, max 60 chars)</span>
                  </Label>
                  <Input
                    value={form.footer}
                    onChange={e => setForm(f => ({ ...f, footer: e.target.value }))}
                    placeholder="Reply STOP to unsubscribe"
                    maxLength={60}
                    className="h-10 rounded-xl text-sm"
                  />
                </div>

                {/* ── BUTTONS ── */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Buttons (Optional)</Label>
                  <div className="flex gap-2">
                    {BUTTON_TYPES.map(bt => (
                      <button
                        key={bt.value}
                        type="button"
                        onClick={() => setForm(f => ({ ...f, buttonType: bt.value, buttons: [] }))}
                        className={`px-3 py-2 rounded-xl border text-xs font-medium transition-all ${form.buttonType === bt.value ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                      >
                        {bt.label}
                      </button>
                    ))}
                  </div>
                  <ButtonEditor
                    buttonType={form.buttonType}
                    buttons={form.buttons}
                    onChange={buttons => setForm(f => ({ ...f, buttons }))}
                  />
                </div>

                {/* ── ACTIVE TOGGLE ── */}
                <div className="flex items-center justify-between p-3.5 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Template</p>
                    <p className="text-xs text-gray-400">Active templates can be used in broadcasts & workflows</p>
                  </div>
                  <Switch
                    checked={form.isActive}
                    onCheckedChange={v => setForm(f => ({ ...f, isActive: v }))}
                    className="data-[state=checked]:bg-emerald-500"
                  />
                </div>

                {/* ── SAVE BUTTONS ── */}
                <div className="flex gap-3 pt-1">
                  <button
                    onClick={closeDialog}
                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-lg shadow-emerald-200/60 disabled:opacity-60 transition-all"
                  >
                    {saving
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                      : editing
                        ? <><Check className="w-4 h-4" /> Update Template</>
                        : <><Send className="w-4 h-4" /> Submit for Approval</>
                    }
                  </button>
                </div>
              </div>

              {/* ── Right: Live Preview ── */}
              <div className="hidden lg:flex w-[380px] flex-shrink-0 bg-gray-50 dark:bg-gray-800/50 flex-col items-center justify-start p-6 overflow-y-auto gap-4">
                <div className="w-full flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Live Preview</p>
                  <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">Real-time</span>
                </div>
                <WhatsAppPreview template={form} previewVars={previewVars} />
                <div className="w-full p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700">
                  <p className="text-xs font-semibold text-gray-500 mb-2">Template Info</p>
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Body length</span>
                      <span className={`font-medium ${(form.body?.length || 0) > 900 ? 'text-red-500' : 'text-gray-700'}`}>{form.body?.length || 0}/1024</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Variables</span>
                      <span className="font-medium text-gray-700">{detectedVars.length}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Buttons</span>
                      <span className="font-medium text-gray-700">{form.buttons?.length || 0}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Header</span>
                      <span className="font-medium text-gray-700 capitalize">{form.headerType}</span>
                    </div>
                    {/* ✅ ADDED: Keyword info in preview panel */}
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Trigger Keyword</span>
                      <span className="font-medium text-violet-600">
                        {keywords.find(k => k.id === form.keywordId)?.keyword || '—'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
